import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { QRCodeSVG } from 'qrcode.react';
import {
  Pencil, ArrowLeft, Baby, Milk as MilkIcon, ArrowLeftRight, HeartPulse, Clock,
  PawPrint, Plus, Tag, Trash2, Upload,
} from 'lucide-react';
import {
  animalsApi, animalIdentificationApi, animalMovementsApi, animalOffspringLinksApi,
  attachmentsApi, breedingRecordsApi, calfRecordsApi, healthRecordsApi, milkSessionsApi,
} from '../../services/resources';
import { getApiErrorMessage } from '../../services/api';
import { useAuth } from '../../hooks/useAuth';
import { useReferenceData } from '../../features/common/referenceData';
import { ILMSCard } from '../../components/ILMSCard';
import { ILMSButton } from '../../components/ILMSButton';
import { ILMSStatusBadge } from '../../components/ILMSStatusBadge';
import { ILMSAlert } from '../../components/ILMSAlert';
import { ILMSSpinner } from '../../components/ILMSLoadingState';
import { ILMSTimeline, type ILMSTimelineItem } from '../../components/ILMSTimeline';
import { ILMSTable, type ILMSTableColumn } from '../../components/ILMSTable';
import { ILMSEmptyState } from '../../components/ILMSEmptyState';
import { ILMSModal } from '../../components/ILMSModal';
import { ILMSFormField, ILMSInput, ILMSSelect } from '../../components/ILMSForm';
import { ILMSDatePicker } from '../../components/ILMSDatePicker';
import { cn } from '../../utils/cn';
import { formatAge, formatCurrency, formatDate, formatNumber, today } from '../../utils/format';
import type {
  Animal, AnimalEvent, AnimalIdentification, AnimalMovement, AnimalOffspringLink,
  Attachment, BreedingRecord, HealthRecord, IdentifierType, MilkSession,
} from '../../types';

type TabId = 'overview' | 'source-acquisition' | 'identification' | 'events' | 'movements' | 'offspring' | 'dairy' | 'breeding' | 'health' | 'attachments';

const TABS: { id: TabId; label: string; adminOnly?: boolean }[] = [
  { id: 'overview', label: 'Overview' },
  { id: 'source-acquisition', label: 'Source & Acquisition', adminOnly: true },
  { id: 'identification', label: 'Identification' },
  { id: 'events', label: 'Events' },
  { id: 'movements', label: 'Movements' },
  { id: 'offspring', label: 'Offspring' },
  { id: 'dairy', label: 'Dairy' },
  { id: 'breeding', label: 'Breeding' },
  { id: 'health', label: 'Health' },
  { id: 'attachments', label: 'Attachments' },
];

const EVENT_ICON: Record<string, React.ComponentType<{ className?: string }>> = {
  Birth: PawPrint,
  Purchase: Tag,
  Transfer: ArrowLeftRight,
  Quarantine: HeartPulse,
  Hospital: HeartPulse,
  Movement: ArrowLeftRight,
  Breeding: Baby,
  Production: MilkIcon,
  Sale: Tag,
  Death: HeartPulse,
  Disposal: HeartPulse,
  StatusChange: Clock,
  Other: Clock,
};

export default function AnimalProfile() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [tab, setTab] = useState<TabId>('overview');
  const { roles, can } = useAuth();
  // Deletion is restricted to exactly Super Admin/Admin — not a general
  // permission, since a hard delete is only safe for a genuine duplicate
  // with no recorded history yet (the backend enforces the same check).
  const canDeleteAnimal = roles.includes('Super Admin') || roles.includes('Admin');
  // Same check the Edit form and the backend's own field-masking use for
  // purchase date/acquisition cost/current value — keeps this tab's
  // visibility consistent with what the API actually returns.
  const canViewCost = can('Purchases', 'Edit');
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const deleteMutation = useMutation({
    mutationFn: () => animalsApi.remove(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['animals'] });
      queryClient.invalidateQueries({ queryKey: ['reference', 'animals'] });
      navigate('/animals');
    },
    onError: (err) => setDeleteError(getApiErrorMessage(err, 'Unable to delete this animal.')),
  });

  const animalQuery = useQuery({
    queryKey: ['animal', id],
    queryFn: () => animalsApi.get(id!),
    enabled: !!id,
  });

  if (animalQuery.isLoading) return <ILMSSpinner label="Loading animal profile…" />;

  if (animalQuery.isError || !animalQuery.data) {
    return (
      <ILMSAlert tone="critical" title="Unable to load this animal">
        {getApiErrorMessage(animalQuery.error, 'This animal could not be found.')}
        <div className="mt-2">
          <ILMSButton size="sm" variant="outline" onClick={() => navigate('/animals')}>
            Back to Animals
          </ILMSButton>
        </div>
      </ILMSAlert>
    );
  }

  const animal = animalQuery.data;

  return (
    <div className="flex flex-col gap-4">
      <button
        onClick={() => navigate('/animals')}
        className="inline-flex items-center gap-1.5 text-secondary text-ink-500 hover:text-brand-green-700 w-fit"
      >
        <ArrowLeft className="h-4 w-4" /> Back to Animals
      </button>

      <ILMSCard>
        <div className="flex flex-col sm:flex-row gap-5">
          <div className="flex gap-4 shrink-0">
            <div className="h-24 w-24 rounded-lg bg-surface-muted overflow-hidden shrink-0 flex items-center justify-center">
              <PawPrint className="h-10 w-10 text-ink-300" />
            </div>
            <div className="h-24 w-24 rounded-lg bg-white border border-ink-300/20 p-2 shrink-0 hidden sm:flex items-center justify-center">
              {animal.qr_code ? <QRCodeSVG value={animal.qr_code} size={80} /> : <span className="text-helper-text text-ink-400 text-center">No QR yet</span>}
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-page-title">{animal.animal_id}</h1>
              <ILMSStatusBadge status={animal.health_status} />
              <ILMSStatusBadge status={animal.production_status} />
            </div>
            <p className="text-secondary text-ink-500 mt-0.5">
              {animal.asset_code ?? '—'} · {animal.animal_type_name ?? 'Animal'}
              {animal.breed_name ? ` · ${animal.breed_name}` : ''}
              {animal.animal_name ? ` · ${animal.animal_name}` : ''}
            </p>

            <dl className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-3 mt-4">
              <ProfileField label="Gender" value={animal.gender} />
              <ProfileField label="Date of Birth" value={formatDate(animal.date_of_birth)} />
              <ProfileField label="Age" value={formatAge(animal.date_of_birth)} />
              <ProfileField label="Weight" value={animal.current_weight_kg ? `${formatNumber(animal.current_weight_kg, 1)} kg` : '—'} />
              <ProfileField label="Color" value={animal.color ?? '—'} />
              <ProfileField label="Added On" value={formatDate(animal.created_at)} />
            </dl>
          </div>

          <div className="shrink-0 flex flex-col gap-2">
            <ILMSButton icon={<Pencil />} variant="outline" onClick={() => navigate(`/animals/${animal.id}/edit`)}>
              Edit
            </ILMSButton>
            {canDeleteAnimal && (
              <ILMSButton icon={<Trash2 />} variant="danger" onClick={() => { setDeleteError(null); setDeleteModalOpen(true); }}>
                Delete
              </ILMSButton>
            )}
          </div>
        </div>
      </ILMSCard>

      <ILMSCard padded={false}>
        <div className="border-b border-ink-300/10 overflow-x-auto no-scrollbar">
          <div className="flex gap-1 px-2 min-w-max">
            {TABS.filter((t) => !t.adminOnly || canViewCost).map((t) => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={cn(
                  'px-3.5 h-12 text-sm font-medium border-b-2 whitespace-nowrap shrink-0',
                  tab === t.id ? 'border-brand-green-600 text-brand-green-700' : 'border-transparent text-ink-500 hover:text-ink-700'
                )}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>
        <div className="p-4 sm:p-5">
          {tab === 'overview' && <OverviewTab animal={animal} />}
          {tab === 'source-acquisition' && canViewCost && <SourceAcquisitionTab animal={animal} />}
          {tab === 'identification' && <IdentificationTab animalId={animal.id} />}
          {tab === 'events' && <EventsTab animalId={animal.id} />}
          {tab === 'movements' && <MovementsTab animalId={animal.id} />}
          {tab === 'offspring' && <OffspringTab animal={animal} />}
          {tab === 'dairy' && <DairyTab animalId={animal.id} />}
          {tab === 'breeding' && <BreedingTab animalId={animal.id} />}
          {tab === 'health' && <HealthTab animalId={animal.id} />}
          {tab === 'attachments' && <AttachmentsTab animalId={animal.id} />}
        </div>
      </ILMSCard>

      <ILMSModal
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        title="Delete this animal?"
        size="sm"
        footer={
          <>
            <ILMSButton variant="ghost" onClick={() => setDeleteModalOpen(false)}>Cancel</ILMSButton>
            <ILMSButton variant="danger" icon={<Trash2 />} loading={deleteMutation.isPending} onClick={() => deleteMutation.mutate()}>
              Delete
            </ILMSButton>
          </>
        }
      >
        <div className="flex flex-col gap-3">
          {deleteError && <ILMSAlert tone="critical">{deleteError}</ILMSAlert>}
          <p className="text-body text-ink-700">
            This is permanent and cannot be undone. It's only allowed if <strong>{animal.animal_id}</strong> has no
            recorded milk, health, breeding, movement, purchase, or sale history yet — if it has left the farm for
            a real reason, use Edit to set its Health Status to Sold/Dead/Disposed/Returned instead.
          </p>
        </div>
      </ILMSModal>
    </div>
  );
}

function ProfileField({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-helper-text text-ink-400">{label}</dt>
      <dd className="text-table-text text-ink-900 font-medium truncate">{value}</dd>
    </div>
  );
}

function OverviewTab({ animal }: { animal: Animal }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <ILMSCard title="Identity" noBorder className="bg-surface-subtle">
        <dl className="grid grid-cols-2 gap-3">
          <ProfileField label="Animal ID" value={animal.animal_id} />
          <ProfileField label="Tag ID" value={animal.primary_tag ?? '—'} />
          <ProfileField label="Asset Code" value={animal.asset_code ?? '—'} />
          <ProfileField label="QR Code" value={animal.qr_code ?? 'Not generated'} />
          <ProfileField label="Health Status" value={<ILMSStatusBadge status={animal.health_status} size="sm" />} />
          <ProfileField label="Production Status" value={<ILMSStatusBadge status={animal.production_status} size="sm" />} />
        </dl>
      </ILMSCard>
      <ILMSCard title="Audit" noBorder className="bg-surface-subtle">
        <dl className="grid grid-cols-2 gap-3">
          <ProfileField label="Created" value={formatDate(animal.created_at)} />
          <ProfileField label="Last Updated" value={formatDate(animal.updated_at)} />
        </dl>
      </ILMSCard>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Source & Acquisition — commercially sensitive (what this animal cost),
// so this whole tab only renders for whoever has Purchases:Edit (Super
// Admin/Admin under today's roles); the backend already blanks these same
// fields to null for anyone else, this just avoids showing an empty tab.
// ---------------------------------------------------------------------------

function SourceAcquisitionTab({ animal }: { animal: Animal }) {
  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <ILMSCard title="Source" noBorder className="bg-surface-subtle">
          <dl className="grid grid-cols-2 gap-3">
            <ProfileField label="Source" value={animal.source} />
            <ProfileField label="Purchase Date" value={animal.purchase_date ? formatDate(animal.purchase_date) : '—'} />
          </dl>
        </ILMSCard>
        <ILMSCard title="Acquisition" noBorder className="bg-surface-subtle">
          <dl className="grid grid-cols-2 gap-3">
            <ProfileField label="Acquisition Cost" value={animal.acquisition_cost ? formatCurrency(animal.acquisition_cost) : '—'} />
            <ProfileField label="Current Value" value={animal.current_value ? formatCurrency(animal.current_value) : '—'} />
          </dl>
        </ILMSCard>
      </div>
      {animal.source === 'Purchased' && (
        <ILMSAlert tone="info">
          Purchase Date and Acquisition Cost come from this animal's record under Finance → Animal Purchases — add
          or correct them there, not here, so there's only one place this cost is ever typed in.
        </ILMSAlert>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Identification
// ---------------------------------------------------------------------------

function IdentificationTab({ animalId }: { animalId: string }) {
  const queryClient = useQueryClient();
  const q = useQuery({
    queryKey: ['animal-identification', animalId],
    queryFn: () => animalIdentificationApi.list({ animal_id: animalId, page_size: 50 }),
  });
  const [open, setOpen] = useState(false);
  // Set when editing an existing row (click it) — null means "Add" mode.
  // identifier_type can't be changed on an existing identifier (the backend
  // update schema doesn't accept it — re-tagging as a different type is a
  // new identifier, not an edit), so it's shown read-only while editing.
  const [editing, setEditing] = useState<AnimalIdentification | null>(null);
  const [type, setType] = useState<IdentifierType>('EarTag');
  const [value, setValue] = useState('');
  const [validFrom, setValidFrom] = useState(today());
  const [isPrimary, setIsPrimary] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const resetForm = () => {
    setEditing(null);
    setType('EarTag');
    setValue('');
    setValidFrom(today());
    setIsPrimary(false);
    setError(null);
  };

  const openAdd = () => {
    resetForm();
    setOpen(true);
  };

  const openEdit = (row: AnimalIdentification) => {
    setEditing(row);
    setType(row.identifier_type);
    setValue(row.identifier_value);
    setValidFrom(row.valid_from);
    setIsPrimary(row.is_primary);
    setError(null);
    setOpen(true);
  };

  const mutation = useMutation({
    mutationFn: () =>
      editing
        ? animalIdentificationApi.update(editing.id, { identifier_value: value, valid_from: validFrom, is_primary: isPrimary })
        : animalIdentificationApi.create({ animal_id: animalId, identifier_type: type, identifier_value: value, valid_from: validFrom, is_primary: isPrimary }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['animal-identification', animalId] });
      setOpen(false);
      resetForm();
    },
    onError: (err) => setError(getApiErrorMessage(err, `Unable to ${editing ? 'update' : 'add'} this identifier.`)),
  });

  const deleteMutation = useMutation({
    mutationFn: () => animalIdentificationApi.remove(editing!.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['animal-identification', animalId] });
      setOpen(false);
      resetForm();
    },
    onError: (err) => setError(getApiErrorMessage(err, 'Unable to remove this identifier.')),
  });

  const columns: ILMSTableColumn<AnimalIdentification>[] = [
    { key: 'type', header: 'Type', primary: true, render: (r) => r.identifier_type },
    { key: 'value', header: 'Value', render: (r) => r.identifier_value },
    { key: 'primary', header: 'Primary', render: (r) => (r.is_primary ? <ILMSStatusBadge status="Active" tone="success" size="sm" /> : '—') },
    { key: 'from', header: 'Valid From', render: (r) => formatDate(r.valid_from) },
    { key: 'to', header: 'Valid To', render: (r) => formatDate(r.valid_to) },
  ];

  return (
    <div className="flex flex-col gap-3">
      <div className="flex justify-end">
        <ILMSButton size="sm" icon={<Plus />} onClick={openAdd}>Add Identifier</ILMSButton>
      </div>
      <ILMSTable
        columns={columns}
        data={q.data?.items ?? []}
        rowKey={(r) => r.id}
        onRowClick={openEdit}
        isLoading={q.isLoading}
        error={q.isError ? getApiErrorMessage(q.error) : null}
        emptyTitle="No identifiers recorded yet."
        emptyDescription="RFID tags, ear tags and QR codes for this animal — including re-tagging history — will appear here."
      />
      <ILMSModal
        open={open}
        onClose={() => { setOpen(false); resetForm(); }}
        title={editing ? 'Edit Identifier' : 'Add Identifier'}
        footer={
          <div className="flex w-full items-center justify-between gap-2">
            {editing ? (
              <ILMSButton variant="danger" size="sm" icon={<Trash2 />} loading={deleteMutation.isPending}
                onClick={() => { setError(null); deleteMutation.mutate(); }}>
                Remove
              </ILMSButton>
            ) : <span />}
            <div className="flex items-center gap-2">
              <ILMSButton variant="ghost" onClick={() => { setOpen(false); resetForm(); }}>Cancel</ILMSButton>
              <ILMSButton loading={mutation.isPending} onClick={() => { setError(null); mutation.mutate(); }} disabled={!value || !validFrom}>
                {editing ? 'Save changes' : 'Add'}
              </ILMSButton>
            </div>
          </div>
        }
      >
        <div className="flex flex-col gap-4">
          {error && <ILMSAlert tone="critical">{error}</ILMSAlert>}
          <ILMSFormField label="Type" htmlFor="identifier_type" required
            helperText={editing ? "Can't be changed on an existing identifier — add a new one instead to re-tag as a different type." : undefined}>
            <ILMSSelect id="identifier_type" value={type} disabled={!!editing} onChange={(e) => setType(e.target.value as IdentifierType)}>
              <option value="RFID">RFID</option>
              <option value="EarTag">Ear Tag</option>
              <option value="QR">QR</option>
            </ILMSSelect>
          </ILMSFormField>
          <ILMSFormField label="Value" htmlFor="identifier_value" required>
            <ILMSInput id="identifier_value" value={value} onChange={(e) => setValue(e.target.value)} />
          </ILMSFormField>
          <ILMSDatePicker label="Valid From" required value={validFrom} onChange={(e) => setValidFrom(e.target.value)} />
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={isPrimary} onChange={(e) => setIsPrimary(e.target.checked)} className="h-5 w-5 rounded border-ink-300/50 text-brand-green-700" />
            <span className="text-body text-ink-700">Set as primary identifier</span>
          </label>
        </div>
      </ILMSModal>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Events (Timeline)
// ---------------------------------------------------------------------------

function EventsTab({ animalId }: { animalId: string }) {
  const q = useQuery({ queryKey: ['animal-events', animalId], queryFn: () => animalsApi.history(animalId) });
  // 'users' is keyed by user id -> username (unlike 'userNames', which is
  // keyed by username itself) - exactly what's needed to resolve an
  // event's created_by into a readable name for "who changed this".
  const { data: refData } = useReferenceData(['users']);
  const usernameFor = (userId?: string | null) => refData.users.find((u) => u.value === userId)?.label;
  if (q.isLoading) return <ILMSSpinner />;
  if (q.isError) return <ILMSAlert tone="critical">{getApiErrorMessage(q.error)}</ILMSAlert>;
  const items: ILMSTimelineItem[] = (q.data ?? []).map((e: AnimalEvent) => ({
    id: e.id,
    date: e.event_date,
    title: e.event_type.replace(/([A-Z])/g, ' $1').trim(),
    description: e.notes || (e.from_status && e.to_status ? `${e.from_status} → ${e.to_status}` : undefined),
    user: usernameFor(e.created_by),
    icon: React.createElement(EVENT_ICON[e.event_type] ?? Clock, { className: 'h-3 w-3' }),
    tone: 'info',
  }));
  return <ILMSTimeline items={items} />;
}

// ---------------------------------------------------------------------------
// Movements
// ---------------------------------------------------------------------------

function MovementsTab({ animalId }: { animalId: string }) {
  const q = useQuery({ queryKey: ['animal-movements', animalId], queryFn: () => animalMovementsApi.list({ animal_id: animalId, page_size: 50 }) });
  const { data: refData } = useReferenceData(['locations']);
  const locName = (id?: string | null) => refData.locations.find((l) => l.value === id)?.label ?? (id ? id.slice(0, 8) : '—');
  const columns: ILMSTableColumn<AnimalMovement>[] = [
    { key: 'date', header: 'Date', primary: true, render: (r) => formatDate(r.movement_date) },
    { key: 'from', header: 'From', render: (r) => locName(r.from_location_id) },
    { key: 'to', header: 'To', render: (r) => locName(r.to_location_id) },
    { key: 'reason', header: 'Reason', render: (r) => r.reason || '—' },
  ];
  return (
    <ILMSTable
      columns={columns}
      data={q.data?.items ?? []}
      rowKey={(r) => r.id}
      isLoading={q.isLoading}
      error={q.isError ? getApiErrorMessage(q.error) : null}
      emptyTitle="No movements recorded yet."
    />
  );
}

// ---------------------------------------------------------------------------
// Offspring
// ---------------------------------------------------------------------------

interface ChildRow {
  key: string;
  label: string;
  otherParentLabel: string;
  birthOrder: number | string;
  pendingTag: boolean;
}

function OffspringTab({ animal }: { animal: Animal }) {
  const queryClient = useQueryClient();
  const { data: refData } = useReferenceData(['animals']);
  const animalLabel = (id?: string | null) => (id ? refData.animals.find((a) => a.value === id)?.label ?? id.slice(0, 8) : '—');

  const asOffspringQuery = useQuery({
    queryKey: ['offspring-links', 'as-offspring', animal.id],
    queryFn: () => animalOffspringLinksApi.list({ animal_id: animal.id, page_size: 20 }),
  });
  const asDamQuery = useQuery({
    queryKey: ['offspring-links', 'as-dam', animal.id],
    queryFn: () => animalOffspringLinksApi.list({ dam_animal_id: animal.id, page_size: 50 }),
  });
  const asSireQuery = useQuery({
    queryKey: ['offspring-links', 'as-sire', animal.id],
    queryFn: () => animalOffspringLinksApi.list({ sire_animal_id: animal.id, page_size: 50 }),
  });
  const children = [...(asDamQuery.data?.items ?? []), ...(asSireQuery.data?.items ?? [])];

  // Calf Records folded in too (on request) — a calf logged there doesn't
  // need a full Animal row to count as offspring (see CalfRecord's own
  // docstring); skip any whose calf_animal_id is already covered above by
  // a real Animal_Offspring_Link row, so a tagged-and-linked calf doesn't
  // show up twice.
  const calfAsDamQuery = useQuery({
    queryKey: ['calf-records', 'as-dam', animal.id],
    queryFn: () => calfRecordsApi.list({ dam_animal_id: animal.id, page_size: 50 }),
  });
  const calfAsSireQuery = useQuery({
    queryKey: ['calf-records', 'as-sire', animal.id],
    queryFn: () => calfRecordsApi.list({ sire_animal_id: animal.id, page_size: 50 }),
  });
  const linkedCalfAnimalIds = new Set(children.map((c) => c.animal_id));
  const calfRecordRows = [...(calfAsDamQuery.data?.items ?? []), ...(calfAsSireQuery.data?.items ?? [])].filter(
    (c) => !c.calf_animal_id || !linkedCalfAnimalIds.has(c.calf_animal_id)
  );

  const childRows: ChildRow[] = [
    ...children.map((r) => ({
      key: r.id,
      label: animalLabel(r.animal_id),
      otherParentLabel: animalLabel(r.dam_animal_id === animal.id ? r.sire_animal_id : r.dam_animal_id),
      birthOrder: r.birth_order ?? '—',
      pendingTag: false,
    })),
    ...calfRecordRows.map((c) => ({
      key: c.id,
      label: c.calf_animal_id ? animalLabel(c.calf_animal_id) : `${c.calf_name || 'Unnamed calf'} (tag pending)`,
      otherParentLabel: animalLabel(c.dam_animal_id === animal.id ? c.sire_animal_id : c.dam_animal_id),
      birthOrder: '—',
      pendingTag: !c.calf_animal_id,
    })),
  ];

  const [open, setOpen] = useState(false);
  const [damId, setDamId] = useState('');
  const [sireId, setSireId] = useState('');
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () => animalOffspringLinksApi.create({ animal_id: animal.id, dam_animal_id: damId || null, sire_animal_id: sireId || null }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['offspring-links', 'as-offspring', animal.id] });
      setOpen(false);
    },
    onError: (err) => setError(getApiErrorMessage(err, 'Unable to save parentage.')),
  });

  const parentColumns: ILMSTableColumn<AnimalOffspringLink>[] = [
    { key: 'dam', header: 'Dam (Mother)', primary: true, render: (r) => animalLabel(r.dam_animal_id) },
    { key: 'sire', header: 'Sire (Father)', render: (r) => animalLabel(r.sire_animal_id) },
    { key: 'order', header: 'Birth Order', render: (r) => r.birth_order ?? '—' },
  ];
  const childColumns: ILMSTableColumn<ChildRow>[] = [
    { key: 'offspring', header: 'Offspring', primary: true, render: (r) => (r.pendingTag ? <span className="text-ink-500 italic">{r.label}</span> : r.label) },
    { key: 'other-parent', header: 'Other Parent', render: (r) => r.otherParentLabel },
    { key: 'order', header: 'Birth Order', render: (r) => r.birthOrder },
  ];

  return (
    <div className="flex flex-col gap-6">
      <div>
        <div className="flex items-center justify-between mb-2">
          <p className="text-card-title">Parents</p>
          <ILMSButton size="sm" icon={<Plus />} onClick={() => setOpen(true)}>Record Parentage</ILMSButton>
        </div>
        <ILMSTable
          columns={parentColumns}
          data={asOffspringQuery.data?.items ?? []}
          rowKey={(r) => r.id}
          isLoading={asOffspringQuery.isLoading}
          error={asOffspringQuery.isError ? getApiErrorMessage(asOffspringQuery.error) : null}
          emptyTitle="No parentage recorded yet."
        />
      </div>
      <div>
        <p className="text-card-title mb-2">Offspring</p>
        <ILMSTable
          columns={childColumns}
          data={childRows}
          rowKey={(r) => r.key}
          isLoading={asDamQuery.isLoading || asSireQuery.isLoading || calfAsDamQuery.isLoading || calfAsSireQuery.isLoading}
          error={asDamQuery.isError || asSireQuery.isError || calfAsDamQuery.isError || calfAsSireQuery.isError ? 'Unable to load offspring.' : null}
          emptyTitle="No offspring recorded yet."
          emptyDescription="Includes calves logged under Dairy → Calf Records, even before they're tagged as their own Animal."
        />
      </div>
      <ILMSModal
        open={open}
        onClose={() => setOpen(false)}
        title="Record Parentage"
        footer={
          <>
            <ILMSButton variant="ghost" onClick={() => setOpen(false)}>Cancel</ILMSButton>
            <ILMSButton loading={mutation.isPending} onClick={() => { setError(null); mutation.mutate(); }} disabled={!damId && !sireId}>
              Save
            </ILMSButton>
          </>
        }
      >
        <div className="flex flex-col gap-4">
          {error && <ILMSAlert tone="critical">{error}</ILMSAlert>}
          <ILMSFormField label="Dam (Mother)" htmlFor="dam" optional>
            <ILMSSelect id="dam" value={damId} onChange={(e) => setDamId(e.target.value)}>
              <option value="">Not recorded</option>
              {refData.animals.map((a) => (
                <option key={a.value} value={a.value}>{a.label}</option>
              ))}
            </ILMSSelect>
          </ILMSFormField>
          <ILMSFormField label="Sire (Father)" htmlFor="sire" optional>
            <ILMSSelect id="sire" value={sireId} onChange={(e) => setSireId(e.target.value)}>
              <option value="">Not recorded</option>
              {refData.animals.map((a) => (
                <option key={a.value} value={a.value}>{a.label}</option>
              ))}
            </ILMSSelect>
          </ILMSFormField>
        </div>
      </ILMSModal>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Dairy / Breeding / Health (light stub tabs — wired to real filtered data)
// ---------------------------------------------------------------------------

function DairyTab({ animalId }: { animalId: string }) {
  const q = useQuery({ queryKey: ['animal-milk', animalId], queryFn: () => milkSessionsApi.list({ animal_id: animalId, page_size: 30 }) });
  const columns: ILMSTableColumn<MilkSession>[] = [
    { key: 'date', header: 'Date', primary: true, render: (r) => formatDate(r.production_date) },
    { key: 'session', header: 'Session', render: (r) => r.session_type },
    { key: 'litres', header: 'Litres', align: 'right', render: (r) => formatNumber(r.litres, 2) },
    { key: 'fat', header: 'Fat %', render: (r) => formatNumber(r.fat_percent, 1), hideOnTablet: true },
    { key: 'snf', header: 'SNF %', render: (r) => formatNumber(r.snf_percent, 1), hideOnTablet: true },
  ];
  return (
    <ILMSTable
      columns={columns}
      data={q.data?.items ?? []}
      rowKey={(r) => r.id}
      isLoading={q.isLoading}
      error={q.isError ? getApiErrorMessage(q.error) : null}
      emptyTitle="No milk sessions recorded yet."
      emptyDescription="Record milk sessions from the Dairy module — they'll show up here automatically."
    />
  );
}

function BreedingTab({ animalId }: { animalId: string }) {
  const q = useQuery({ queryKey: ['animal-breeding', animalId], queryFn: () => breedingRecordsApi.list({ animal_id: animalId, page_size: 30 }) });
  const columns: ILMSTableColumn<BreedingRecord>[] = [
    { key: 'type', header: 'Type', primary: true, render: (r) => r.breeding_type },
    { key: 'date', header: 'AI Date', render: (r) => formatDate(r.ai_date) },
    { key: 'technician', header: 'Technician', render: (r) => r.technician || '—', hideOnTablet: true },
    { key: 'bull', header: 'Bull', render: (r) => r.bull_name || '—' },
    { key: 'outcome', header: 'Outcome', render: (r) => (r.outcome ? <ILMSStatusBadge status={r.outcome} size="sm" /> : '—') },
  ];
  return (
    <ILMSTable
      columns={columns}
      data={q.data?.items ?? []}
      rowKey={(r) => r.id}
      isLoading={q.isLoading}
      error={q.isError ? getApiErrorMessage(q.error) : null}
      emptyTitle="No breeding records yet."
      emptyDescription="Heat, service and pregnancy records will appear here — see the Breeding module for pregnancy and calving history."
    />
  );
}

function HealthTab({ animalId }: { animalId: string }) {
  const q = useQuery({ queryKey: ['animal-health', animalId], queryFn: () => healthRecordsApi.list({ animal_id: animalId, page_size: 30 }) });
  const columns: ILMSTableColumn<HealthRecord>[] = [
    { key: 'date', header: 'Date', primary: true, render: (r) => formatDate(r.record_date) },
    { key: 'disease', header: 'Disease', render: (r) => r.disease || '—' },
    { key: 'severity', header: 'Severity', render: (r) => r.severity || '—' },
    { key: 'diagnosis', header: 'Diagnosis', render: (r) => r.diagnosis || '—', hideOnTablet: true },
    { key: 'outcome', header: 'Outcome', render: (r) => r.outcome || '—' },
  ];
  return (
    <ILMSTable
      columns={columns}
      data={q.data?.items ?? []}
      rowKey={(r) => r.id}
      isLoading={q.isLoading}
      error={q.isError ? getApiErrorMessage(q.error) : null}
      emptyTitle="No health records yet."
      emptyDescription="Diagnoses, treatments and vaccinations are managed from the Health module and will appear here."
    />
  );
}

// ---------------------------------------------------------------------------
// Attachments
// ---------------------------------------------------------------------------

function AttachmentsTab({ animalId }: { animalId: string }) {
  const queryClient = useQueryClient();
  const q = useQuery({
    queryKey: ['animal-attachments', animalId],
    queryFn: () => attachmentsApi.list({ entity_type: 'Animal', entity_id: animalId, page_size: 50 }),
  });
  const [error, setError] = useState<string | null>(null);

  const uploadMutation = useMutation({
    mutationFn: (file: File) => attachmentsApi.upload(file, 'Animal', animalId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['animal-attachments', animalId] }),
    onError: (err) => setError(getApiErrorMessage(err, 'Unable to upload this file.')),
  });

  const columns: ILMSTableColumn<Attachment>[] = [
    { key: 'name', header: 'File', primary: true, render: (a) => a.file_name },
    { key: 'type', header: 'Type', render: (a) => a.file_type },
    { key: 'size', header: 'Size', render: (a) => `${(a.file_size / 1024).toFixed(1)} KB`, hideOnTablet: true },
    { key: 'uploaded_at', header: 'Uploaded', render: (a) => formatDate(a.uploaded_at) },
  ];

  return (
    <div className="flex flex-col gap-3">
      {error && <ILMSAlert tone="critical">{error}</ILMSAlert>}
      <div className="flex justify-end">
        <label className="inline-flex">
          <input
            type="file"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) uploadMutation.mutate(file);
              e.target.value = '';
            }}
          />
          <span className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-md text-sm font-semibold bg-brand-green-700 text-white hover:bg-brand-green-800 cursor-pointer">
            <Upload className="h-4 w-4" /> {uploadMutation.isPending ? 'Uploading…' : 'Upload File'}
          </span>
        </label>
      </div>
      <ILMSTable
        columns={columns}
        data={q.data?.items ?? []}
        rowKey={(a) => a.id}
        isLoading={q.isLoading}
        error={q.isError ? getApiErrorMessage(q.error) : null}
        emptyTitle="No attachments yet."
        emptyDescription="Photos, documents and certificates for this animal will appear here."
      />
    </div>
  );
}
